import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Flex,
  FormControl,
  FormLabel,
  Button,
  Textarea,
  Input,
  Select,
  Icon,
  SimpleGrid,
  useColorModeValue,
  keyframes,
  Badge,
  InputGroup,
  InputLeftElement,
  ScaleFade,
  SlideFade,
  Fade,
} from '@chakra-ui/react';
import { useCustomToast } from '../utils/customToast';
import {
  FaPaperPlane,
  FaLightbulb,
  FaBug,
  FaComments,
  FaRocket,
  FaUser,
  FaEnvelope,
  FaTag,
  FaCheckCircle,
  FaStar,
  FaHeart,
  FaMagic,
} from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import { submitRoadmapFeedback } from '../services/roadmapService';
import { logger } from '../utils/logger';
import { useUser } from '../contexts/UserContext';

// Keyframe animations
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  25% { transform: translateY(-10px) rotate(2deg); }
  50% { transform: translateY(-5px) rotate(0deg); }
  75% { transform: translateY(-15px) rotate(-2deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.05); opacity: 1; }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.3); }
  50% { box-shadow: 0 0 40px rgba(99, 102, 241, 0.6); }
`;

const slideInUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(30px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const scaleIn = keyframes`
  from { 
    opacity: 0; 
    transform: scale(0.9); 
  }
  to { 
    opacity: 1; 
    transform: scale(1); 
  }
`;

const successPop = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); opacity: 1; }
`;

const confetti = keyframes`
  0% { transform: translateY(0) rotate(0deg); opacity: 1; }
  100% { transform: translateY(-100px) rotate(720deg); opacity: 0; }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Feedback type cards data
const feedbackTypes = [
  {
    id: 'feature_request',
    title: 'Feature Request',
    description: 'Suggest new features or capabilities',
    icon: FaLightbulb,
    color: 'purple',
    gradient: 'linear(to-br, purple.400, pink.400)',
  },
  {
    id: 'improvement',
    title: 'Improvement',
    description: 'Ideas to enhance existing features',
    icon: FaRocket,
    color: 'blue',
    gradient: 'linear(to-br, blue.400, cyan.400)',
  },
  {
    id: 'bug_report',
    title: 'Bug Report',
    description: 'Report issues or unexpected behavior',
    icon: FaBug,
    color: 'orange',
    gradient: 'linear(to-br, orange.400, red.400)',
  },
  {
    id: 'general',
    title: 'General',
    description: 'General feedback or questions',
    icon: FaComments,
    color: 'teal',
    gradient: 'linear(to-br, teal.400, green.400)',
  },
];

// Floating particle component
const FloatingParticle = ({ delay, duration, size, left, top, color }) => (
  <Box
    position="absolute"
    left={left}
    top={top}
    w={size}
    h={size}
    borderRadius="full"
    bg={color}
    opacity={0.6}
    animation={`${float} ${duration}s ease-in-out ${delay}s infinite`}
    pointerEvents="none"
  />
);

// Feedback type card component
const FeedbackTypeCard = ({ type, isSelected, onClick, index }) => {
  const bgSelected = useColorModeValue('white', 'gray.800');
  const bgUnselected = useColorModeValue('whiteAlpha.600', 'whiteAlpha.100');
  const borderSelected = `${type.color}.400`;
  const borderUnselected = useColorModeValue('whiteAlpha.400', 'whiteAlpha.200');

  return (
    <Box
      as="button"
      onClick={onClick}
      p={5}
      borderRadius="2xl"
      bg={isSelected ? bgSelected : bgUnselected}
      backdropFilter="blur(10px)"
      border="2px solid"
      borderColor={isSelected ? borderSelected : borderUnselected}
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      transform={isSelected ? 'scale(1.02)' : 'scale(1)'}
      boxShadow={isSelected ? `0 10px 40px -10px var(--chakra-colors-${type.color}-400)` : 'none'}
      _hover={{
        transform: 'scale(1.02)',
        borderColor: `${type.color}.300`,
        boxShadow: `0 10px 40px -10px var(--chakra-colors-${type.color}-400)`,
      }}
      animation={`${slideInUp} 0.5s ease-out ${index * 0.1}s both`}
      w="full"
      textAlign="left"
      position="relative"
      overflow="hidden"
    >
      {isSelected && (
        <Box
          position="absolute"
          top={2}
          right={2}
          animation={`${successPop} 0.3s ease-out`}
        >
          <Icon as={FaCheckCircle} color={`${type.color}.400`} boxSize={5} />
        </Box>
      )}
      <VStack align="start" spacing={3}>
        <Flex
          w="48px"
          h="48px"
          borderRadius="xl"
          bgGradient={type.gradient}
          justify="center"
          align="center"
          boxShadow={`0 4px 20px -4px var(--chakra-colors-${type.color}-400)`}
          animation={isSelected ? `${pulse} 2s ease-in-out infinite` : 'none'}
        >
          <Icon as={type.icon} color="white" boxSize={5} />
        </Flex>
        <Box>
          <Text fontWeight="bold" fontSize="md" color={useColorModeValue('gray.800', 'white')}>
            {type.title}
          </Text>
          <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.400')} mt={1}>
            {type.description}
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};

// Success animation component
const SuccessAnimation = ({ onReset }) => {
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box
      position="relative"
      textAlign="center"
      py={12}
      px={8}
      animation={`${scaleIn} 0.5s ease-out`}
    >
      {/* Confetti particles */}
      {showConfetti && (
        <>
          {[...Array(12)].map((_, i) => (
            <Box
              key={i}
              position="absolute"
              left={`${20 + Math.random() * 60}%`}
              top="40%"
              w="10px"
              h="10px"
              borderRadius={Math.random() > 0.5 ? 'full' : 'sm'}
              bg={['purple.400', 'pink.400', 'blue.400', 'cyan.400', 'green.400', 'orange.400'][i % 6]}
              animation={`${confetti} ${1 + Math.random()}s ease-out ${Math.random() * 0.5}s forwards`}
            />
          ))}
        </>
      )}

      <VStack spacing={6}>
        <Box
          w="100px"
          h="100px"
          borderRadius="full"
          bgGradient="linear(to-br, green.400, teal.400)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="0 10px 40px -10px var(--chakra-colors-green-400)"
          animation={`${successPop} 0.6s ease-out`}
        >
          <Icon as={FaCheckCircle} color="white" boxSize={12} />
        </Box>

        <VStack spacing={2}>
          <Heading size="lg" bgGradient="linear(to-r, green.400, teal.400)" bgClip="text">
            Thank You!
          </Heading>
          <Text color={useColorModeValue('gray.600', 'gray.400')} maxW="sm">
            Your feedback has been submitted successfully. We truly appreciate you taking the time to help us improve!
          </Text>
        </VStack>

        <HStack spacing={2} pt={4}>
          <Icon as={FaHeart} color="pink.400" />
          <Text fontSize="sm" color={useColorModeValue('gray.500', 'gray.500')}>
            Sent to team@localytics.co
          </Text>
        </HStack>

        <Button
          mt={4}
          variant="outline"
          colorScheme="green"
          onClick={onReset}
          leftIcon={<Icon as={FaMagic} />}
          _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
          transition="all 0.2s"
        >
          Submit Another
        </Button>
      </VStack>
    </Box>
  );
};

// Main FeedbackPage component
const FeedbackPage = () => {
  const { brandName } = useUser();
  const [feedbackType, setFeedbackType] = useState('feature_request');
  const [priority, setPriority] = useState('medium');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const toast = useCustomToast();

  // Theme colors
  const cardBg = useColorModeValue('whiteAlpha.800', 'whiteAlpha.100');
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const inputBg = useColorModeValue('white', 'whiteAlpha.100');
  const inputBorder = useColorModeValue('gray.200', 'whiteAlpha.300');

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate inputs
    if (!name || !email || !message) {
      toast({
        title: 'Missing information',
        description: 'Please fill in all required fields',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    setIsSubmitting(true);

    const feedbackData = {
      feedbackType,
      priority,
      name,
      email,
      subject: subject || `[${feedbackType.replace('_', ' ').toUpperCase()}] Feedback from ${name}`,
      message,
    };

    try {
      const responseData = await submitRoadmapFeedback(feedbackData);
      setIsSubmitting(false);
      setIsSubmitted(true);

      toast({
        title: 'Feedback submitted!',
        description: responseData.message || 'Thank you for your feedback!',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } catch (error) {
      setIsSubmitting(false);
      logger.error('Error submitting feedback:', error);
      toast({
        title: 'Submission failed',
        description: 'Could not submit feedback. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const handleReset = () => {
    setFeedbackType('feature_request');
    setPriority('medium');
    setName('');
    setEmail('');
    setSubject('');
    setMessage('');
    setIsSubmitted(false);
  };

  return (
    <Box
      minH="100vh"
      position="relative"
      overflow="hidden"
      bg={useColorModeValue('gray.50', 'gray.900')}
    >
      {/* Animated gradient background */}
      <Box
        position="absolute"
        top="-50%"
        left="-25%"
        w="150%"
        h="200%"
        bgGradient="linear(135deg, purple.100, blue.50, pink.100, cyan.50)"
        opacity={useColorModeValue(0.6, 0.1)}
        animation={`${gradientMove} 15s ease infinite`}
        backgroundSize="400% 400%"
        pointerEvents="none"
      />

      {/* Floating particles */}
      <FloatingParticle delay={0} duration={6} size="12px" left="10%" top="20%" color="purple.300" />
      <FloatingParticle delay={1} duration={8} size="8px" left="85%" top="15%" color="blue.300" />
      <FloatingParticle delay={2} duration={7} size="16px" left="75%" top="60%" color="pink.300" />
      <FloatingParticle delay={0.5} duration={9} size="10px" left="15%" top="70%" color="cyan.300" />
      <FloatingParticle delay={1.5} duration={6} size="14px" left="90%" top="80%" color="purple.200" />
      <FloatingParticle delay={3} duration={8} size="8px" left="5%" top="50%" color="blue.200" />

      {/* Main content */}
      <Container maxW="1000px" py={12} position="relative" zIndex={1}>
        {/* Header section */}
        <VStack spacing={4} mb={10} animation={`${slideInUp} 0.6s ease-out`}>
          <HStack spacing={3}>
            <Flex
              w="56px"
              h="56px"
              borderRadius="xl"
              bgGradient="linear(to-br, purple.500, pink.500)"
              justify="center"
              align="center"
              boxShadow="0 8px 32px -8px var(--chakra-colors-purple-500)"
              animation={`${pulse} 3s ease-in-out infinite`}
            >
              <Icon as={HiSparkles} color="white" boxSize={7} />
            </Flex>
            <Heading
              as="h1"
              size="2xl"
              bgGradient="linear(to-r, purple.500, pink.500, blue.500)"
              bgClip="text"
              fontWeight="extrabold"
            >
              Share Feedback
            </Heading>
          </HStack>
          <Text
            color={subtitleColor}
            fontSize="lg"
            textAlign="center"
            maxW="lg"
          >
            Help us build the future of {brandName}. Your ideas and feedback shape our roadmap.
          </Text>

          {/* Decorative badge */}
          <Badge
            px={4}
            py={2}
            borderRadius="full"
            bgGradient="linear(to-r, purple.100, pink.100)"
            color="purple.700"
            fontSize="sm"
            fontWeight="medium"
            animation={`${shimmer} 3s linear infinite`}
            backgroundSize="200% 100%"
          >
            <HStack spacing={2}>
              <Icon as={FaStar} color="purple.500" />
              <Text>We read every submission</Text>
              <Icon as={FaStar} color="pink.500" />
            </HStack>
          </Badge>
        </VStack>

        {/* Main card */}
        <Box
          bg={cardBg}
          backdropFilter="blur(20px)"
          borderRadius="3xl"
          border="1px solid"
          borderColor={useColorModeValue('whiteAlpha.800', 'whiteAlpha.200')}
          boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.15)"
          overflow="hidden"
          animation={`${scaleIn} 0.5s ease-out 0.2s both, ${glow} 4s ease-in-out infinite`}
        >
          {isSubmitted ? (
            <SuccessAnimation onReset={handleReset} />
          ) : (
            <Box p={{ base: 6, md: 10 }}>
              <form onSubmit={handleSubmit}>
                <VStack spacing={8} align="stretch">
                  {/* Feedback type selection */}
                  <Box>
                    <Text
                      fontWeight="semibold"
                      color={textColor}
                      mb={4}
                      fontSize="lg"
                    >
                      What type of feedback do you have?
                    </Text>
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4}>
                      {feedbackTypes.map((type, index) => (
                        <FeedbackTypeCard
                          key={type.id}
                          type={type}
                          isSelected={feedbackType === type.id}
                          onClick={() => setFeedbackType(type.id)}
                          index={index}
                        />
                      ))}
                    </SimpleGrid>
                  </Box>

                  {/* Form fields */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <FormControl isRequired>
                      <FormLabel color={textColor} fontWeight="medium">
                        Your Name
                      </FormLabel>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <Icon
                            as={FaUser}
                            color={focusedField === 'name' ? 'purple.400' : 'gray.400'}
                            transition="color 0.2s"
                          />
                        </InputLeftElement>
                        <Input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          onFocus={() => setFocusedField('name')}
                          onBlur={() => setFocusedField(null)}
                          placeholder="John Doe"
                          bg={inputBg}
                          border="2px solid"
                          borderColor={focusedField === 'name' ? 'purple.400' : inputBorder}
                          borderRadius="xl"
                          _hover={{ borderColor: 'purple.300' }}
                          _focus={{
                            borderColor: 'purple.400',
                            boxShadow: '0 0 0 3px var(--chakra-colors-purple-100)',
                          }}
                          transition="all 0.2s"
                          size="lg"
                        />
                      </InputGroup>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel color={textColor} fontWeight="medium">
                        Email Address
                      </FormLabel>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <Icon
                            as={FaEnvelope}
                            color={focusedField === 'email' ? 'purple.400' : 'gray.400'}
                            transition="color 0.2s"
                          />
                        </InputLeftElement>
                        <Input
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onFocus={() => setFocusedField('email')}
                          onBlur={() => setFocusedField(null)}
                          placeholder="john@example.com"
                          type="email"
                          bg={inputBg}
                          border="2px solid"
                          borderColor={focusedField === 'email' ? 'purple.400' : inputBorder}
                          borderRadius="xl"
                          _hover={{ borderColor: 'purple.300' }}
                          _focus={{
                            borderColor: 'purple.400',
                            boxShadow: '0 0 0 3px var(--chakra-colors-purple-100)',
                          }}
                          transition="all 0.2s"
                          size="lg"
                        />
                      </InputGroup>
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <FormControl>
                      <FormLabel color={textColor} fontWeight="medium">
                        Subject (Optional)
                      </FormLabel>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <Icon
                            as={FaTag}
                            color={focusedField === 'subject' ? 'purple.400' : 'gray.400'}
                            transition="color 0.2s"
                          />
                        </InputLeftElement>
                        <Input
                          value={subject}
                          onChange={(e) => setSubject(e.target.value)}
                          onFocus={() => setFocusedField('subject')}
                          onBlur={() => setFocusedField(null)}
                          placeholder="Brief summary"
                          bg={inputBg}
                          border="2px solid"
                          borderColor={focusedField === 'subject' ? 'purple.400' : inputBorder}
                          borderRadius="xl"
                          _hover={{ borderColor: 'purple.300' }}
                          _focus={{
                            borderColor: 'purple.400',
                            boxShadow: '0 0 0 3px var(--chakra-colors-purple-100)',
                          }}
                          transition="all 0.2s"
                          size="lg"
                        />
                      </InputGroup>
                    </FormControl>

                    <FormControl>
                      <FormLabel color={textColor} fontWeight="medium">
                        Priority
                      </FormLabel>
                      <Select
                        value={priority}
                        onChange={(e) => setPriority(e.target.value)}
                        bg={inputBg}
                        border="2px solid"
                        borderColor={inputBorder}
                        borderRadius="xl"
                        _hover={{ borderColor: 'purple.300' }}
                        _focus={{
                          borderColor: 'purple.400',
                          boxShadow: '0 0 0 3px var(--chakra-colors-purple-100)',
                        }}
                        transition="all 0.2s"
                        size="lg"
                      >
                        <option value="low">Low Priority</option>
                        <option value="medium">Medium Priority</option>
                        <option value="high">High Priority</option>
                        <option value="critical">Critical</option>
                      </Select>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl isRequired>
                    <FormLabel color={textColor} fontWeight="medium">
                      Your Message
                    </FormLabel>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Share your thoughts, ideas, or describe the issue in detail..."
                      bg={inputBg}
                      border="2px solid"
                      borderColor={focusedField === 'message' ? 'purple.400' : inputBorder}
                      borderRadius="xl"
                      _hover={{ borderColor: 'purple.300' }}
                      _focus={{
                        borderColor: 'purple.400',
                        boxShadow: '0 0 0 3px var(--chakra-colors-purple-100)',
                      }}
                      transition="all 0.2s"
                      rows={6}
                      resize="vertical"
                      size="lg"
                    />
                  </FormControl>

                  {/* Submit button */}
                  <Flex justify="center" pt={4}>
                    <Button
                      type="submit"
                      size="lg"
                      px={12}
                      py={7}
                      fontSize="lg"
                      fontWeight="bold"
                      bgGradient="linear(to-r, purple.500, pink.500)"
                      color="white"
                      borderRadius="2xl"
                      isLoading={isSubmitting}
                      loadingText="Sending..."
                      rightIcon={<Icon as={FaPaperPlane} />}
                      _hover={{
                        bgGradient: 'linear(to-r, purple.600, pink.600)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 20px 40px -12px var(--chakra-colors-purple-500)',
                      }}
                      _active={{
                        transform: 'translateY(-1px)',
                      }}
                      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                      boxShadow="0 10px 30px -10px var(--chakra-colors-purple-500)"
                    >
                      Send Feedback
                    </Button>
                  </Flex>
                </VStack>
              </form>
            </Box>
          )}
        </Box>

        {/* Footer info */}
        <VStack spacing={3} mt={8} animation={`${slideInUp} 0.6s ease-out 0.4s both`}>
          <HStack
            spacing={2}
            color={subtitleColor}
            fontSize="sm"
            bg={useColorModeValue('whiteAlpha.600', 'whiteAlpha.100')}
            px={4}
            py={2}
            borderRadius="full"
            backdropFilter="blur(10px)"
          >
            <Icon as={FaHeart} color="pink.400" />
            <Text>Your feedback is sent directly to our team</Text>
          </HStack>
          <Text fontSize="xs" color={subtitleColor}>
            We typically respond within 24-48 hours
          </Text>
        </VStack>
      </Container>
    </Box>
  );
};

export default FeedbackPage;
